/**
 * Referral Prompt Component
 * =========================
 * Shows after script generation when user is low on scripts.
 */

import { useState } from "react";
import { X, Copy, Check, Gift, Sparkles } from "lucide-react";
import { xhrGet, API } from "../utils/api";

export function ReferralPrompt({ isOpen, onClose, remaining }) {
  const [referralLink, setReferralLink] = useState(null);
  const [copied, setCopied] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const fetchReferralLink = async () => {
    if (referralLink) return;
    
    setIsLoading(true);
    try {
      const res = await xhrGet(`${API}/referral/info`);
      if (res.status === 200) {
        setReferralLink(res.data?.referral_link);
      }
    } catch (err) {
      console.error("Failed to fetch referral link:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const copyToClipboard = async () => {
    if (!referralLink) {
      await fetchReferralLink();
      return;
    }
    
    try {
      await navigator.clipboard.writeText(referralLink);
      setCopied(true);
      setTimeout(() => {
        setCopied(false);
        onClose();
      }, 1500);
    } catch (err) {
      const textarea = document.createElement('textarea');
      textarea.value = referralLink;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setCopied(true);
      setTimeout(() => {
        setCopied(false);
        onClose();
      }, 1500);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 animate-fade-in"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:bottom-4 md:w-96 bg-zinc-900 border border-zinc-800 rounded-2xl shadow-2xl z-50 animate-slide-up overflow-hidden">
        {/* Gradient accent */}
        <div className="h-1 bg-gradient-to-r from-violet-500 via-pink-500 to-amber-500" />
        
        <div className="p-5">
          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute top-3 right-3 w-7 h-7 flex items-center justify-center rounded-lg hover:bg-zinc-800 transition-colors"
          >
            <X className="w-4 h-4 text-zinc-400" />
          </button>

          {/* Icon */}
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-violet-500/20 to-pink-500/20 flex items-center justify-center mb-4">
            <Gift className="w-6 h-6 text-violet-400" />
          </div>

          {/* Content */}
          <h3 className="text-lg font-bold mb-1">
            {remaining <= 0 ? "Out of scripts?" : `Only ${remaining} script${remaining === 1 ? '' : 's'} left today`}
          </h3>
          <p className="text-sm text-zinc-400 mb-4">
            Invite a creator and unlock <span className="text-violet-400 font-semibold">+5 bonus scripts</span> instantly!
          </p>

          {/* Copy button */}
          <button
            onClick={copyToClipboard}
            disabled={isLoading}
            className={`w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl font-semibold transition-all ${
              copied 
                ? "bg-green-500 text-white" 
                : "bg-violet-600 hover:bg-violet-500 text-white"
            }`}
          >
            {isLoading ? (
              <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            ) : copied ? (
              <>
                <Check className="w-4 h-4" />
                Link Copied!
              </>
            ) : (
              <>
                <Copy className="w-4 h-4" />
                Copy Referral Link
              </>
            )}
          </button>

          {/* Bonus info */}
          <div className="flex items-center justify-center gap-2 mt-3 text-xs text-zinc-500">
            <Sparkles className="w-3 h-3" />
            <span>Bonus scripts never expire</span>
          </div>
        </div>
      </div>
    </>
  );
}

export default ReferralPrompt;
